import { useRef } from "react";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { BookmarkSimple, ChatCircleText, ShareNetwork, LockSimple } from "@phosphor-icons/react";
import { EvChart } from "./EvChart";

const RISK_COLORS = { safe: "#00E676", medium: "#F59E0B", lotto: "#FF3366" };

export const EvBadge = ({ ev }) => {
  const positive = ev >= 0;
  return (
    <span data-testid="ev-badge"
      className={`rounded-full px-2.5 py-0.5 text-xs font-bold border ${
        positive
          ? "bg-green-500/10 text-green-400 border-green-500/30 drop-shadow-[0_0_8px_rgba(0,230,118,0.3)]"
          : "bg-red-500/10 text-[#FF3366] border-red-500/30"
      }`}>
      {positive ? "+" : ""}{(ev * 100).toFixed(1)}% EV
    </span>
  );
};

export const ParlayCard = ({ parlay, index, onSave, saved }) => {
  const color = RISK_COLORS[parlay.risk] || "#2563EB";
  const cardRef = useRef(null);

  const share = async () => {
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#141419", scale: 2, useCORS: true });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "parlaylabs-ticket.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My ParlayLabs ticket" });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `parlaylabs-${parlay.combined_american}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Ticket image downloaded — share it anywhere");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create ticket image");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", damping: 20, stiffness: 100, delay: index * 0.15 }}
      className="bg-[#141419] border border-white/10 rounded-xl overflow-hidden"
      data-testid={`parlay-card-${index}`}>
      <div ref={cardRef} className="bg-[#141419]">
      <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">
            Parlay {index + 1} · <span style={{ color }}>{parlay.risk}</span>
          </p>
          <p className="font-display text-4xl font-black tracking-tight" data-testid={`parlay-odds-${index}`}>
            {parlay.combined_american}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            ${100} pays <span className="text-white font-bold">${(100 + parlay.payout_per_100).toFixed(0)}</span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {parlay.ev !== undefined ? <EvBadge ev={parlay.ev} /> : (
            <span data-testid="ev-locked-badge"
              className="rounded-full px-2.5 py-0.5 text-xs font-bold border bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30 flex items-center gap-1">
              <LockSimple size={12} weight="bold" /> EV · PRO
            </span>
          )}
          {parlay.true_prob !== undefined && (
            <div className="text-right">
              <p className="text-xs text-slate-500">Hit probability</p>
              <p className="text-sm font-bold" style={{ color }} data-testid={`parlay-prob-${index}`}>
                {(parlay.true_prob * 100).toFixed(1)}%
              </p>
            </div>
          )}
        </div>
      </div>

      {parlay.confidence !== undefined && (
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Confidence</span>
          <span className="text-xs font-bold" style={{ color }}>{parlay.confidence}%</span>
        </div>
        <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-700"
            style={{ width: `${parlay.confidence}%`, background: color }} />
        </div>
      </div>
      )}

      {parlay.legs?.[0]?.true_prob !== undefined && <EvChart legs={parlay.legs} color={color} />}

      <ul className="px-5 py-3 space-y-3">
        {parlay.legs.map((leg, i) => (
          <li key={i} className="flex items-center justify-between gap-3" data-testid={`parlay-${index}-leg-${i}`}>
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{leg.pick}</p>
              <p className="text-xs text-slate-500 truncate">{leg.market} · {leg.matchup}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold font-display tracking-wide">{leg.american_str}</p>
              {leg.true_prob !== undefined && <p className="text-[10px] text-slate-500">{(leg.true_prob * 100).toFixed(0)}% win</p>}
            </div>
          </li>
        ))}
      </ul>

      {parlay.explanation && (
        <div className="mx-5 mb-4 pl-3 border-l-2" style={{ borderColor: color }}>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1">
            <ChatCircleText size={14} weight="bold" /> Coach's note
          </p>
          <p className="text-sm text-slate-400 italic leading-relaxed" data-testid={`parlay-explanation-${index}`}>
            {parlay.explanation}
          </p>
        </div>
      )}

      </div>

      <div className="px-5 pb-5 flex gap-2">
        {onSave && (
          <button onClick={() => onSave(parlay)} disabled={saved} data-testid={`save-parlay-btn-${index}`}
            className={`flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold transition-colors ${
              saved ? "bg-white/5 text-slate-500 cursor-default"
                : "bg-white/10 hover:bg-[#2563EB] text-white"
            }`}>
            <BookmarkSimple size={18} weight={saved ? "fill" : "bold"} />
            {saved ? "Saved" : "Save"}
          </button>
        )}
        <button onClick={share} data-testid={`share-parlay-btn-${index}`}
          className="flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold bg-white/10 hover:bg-[#00E676] hover:text-black transition-colors">
          <ShareNetwork size={18} weight="bold" /> Share
        </button>
      </div>
    </motion.div>
  );
};
